import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/auth-context";
import { useWeekPlan } from "@/context/week-plan-context";
import { Button } from "@/components/ui/button";
import { PersonAvatar } from "@/components/person-avatar";
import { editAckKey, formatEditedAt, shouldShowEditedBanner } from "@/lib/edit-attribution";
import { cn } from "@/lib/utils";

function readAck(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeAck(key: string, editedAt: string): void {
  try {
    localStorage.setItem(key, editedAt);
  } catch {
    // Private mode or full storage: the ack still holds for this session.
  }
}

/** The current time, refreshed every `ms` while `active` ("hace 5 min" keeps counting). */
function useNow(active: boolean, ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!active) return;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(id);
  }, [active, ms]);
  return now;
}

interface Shown {
  name: string;
  editedAt: string;
}

/**
 * "romina actualizó tu semana · hoy, 21:40 [Entendido]" (design Q4, Screen 7):
 * on my own week, while someone else edited it after the last edit I
 * acknowledged on this device. It stays until Entendido: my own later saves
 * (ticking "comido", say) don't clear it.
 *
 * Rendered by WeekLayout as the first item of the content stack (`gap-4`);
 * collapsed, a negative margin cancels its gap so it takes no space. It shows
 * up already open with the week; one that arrives later (a refetch on return,
 * a save's 409) slides open, and Entendido slides it shut, instead of jolting
 * the day below.
 */
export function EditedByBanner() {
  const { state } = useWeekPlan();
  const { user } = useAuth();
  const weekId = state.loadedWeekId;
  const meta = state.weekMeta;

  const key = user && weekId ? editAckKey(user.id, weekId) : null;
  const storedAck = useMemo(() => (key ? readAck(key) : null), [key]);
  // This session's ack: hides the banner even when storage is unavailable.
  const [sessionAck, setSessionAck] = useState<{ key: string; editedAt: string } | null>(null);
  const ackedEditedAt = sessionAck && sessionAck.key === key ? sessionAck.editedAt : storedAck;

  const wanted = !!user && shouldShowEditedBanner(meta, user.id, ackedEditedAt);
  const current: Shown | null = wanted ? { name: meta.plannerEdit!.by.username, editedAt: meta.plannerEdit!.at } : null;

  // What the card says; it outlives `wanted` so the text stays put while the
  // card collapses.
  const [shown, setShown] = useState<Shown | null>(current);
  if (current && (!shown || shown.editedAt !== current.editedAt || shown.name !== current.name)) {
    setShown(current);
  }
  const open = wanted && shown !== null;

  // Open without a transition when the week first appears with it; animate
  // an arrival afterwards (render collapsed, force the layout, then expand).
  const [expanded, setExpanded] = useState(open);
  const frameRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (open && !expanded) {
      void frameRef.current?.offsetHeight;
      setExpanded(true);
    } else if (!open && expanded) {
      setExpanded(false);
    }
  }, [open, expanded]);

  const now = useNow(open);
  if (!shown) return null;

  const at = new Date(shown.editedAt);
  const visible = open && expanded;

  const acknowledge = () => {
    if (!key || !current) return;
    writeAck(key, current.editedAt);
    setSessionAck({ key, editedAt: current.editedAt });
  };

  return (
    <div
      ref={frameRef}
      inert={!visible}
      className={cn(
        "grid shrink-0 transition-[grid-template-rows,margin,opacity] duration-200 ease-out motion-reduce:transition-none",
        // pb-1 inside the clip keeps the card's shadow; -mb-1 gives it back.
        visible ? "-mb-1 grid-rows-[1fr] opacity-100" : "-mb-4 grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="pb-1">
          <section
            aria-label="Cambios en tu semana"
            className="flex items-start gap-3 rounded-xl border bg-card p-3 text-card-foreground shadow-sm"
          >
            <PersonAvatar name={shown.name} tone="friend" />
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-5 [overflow-wrap:anywhere]">
                <span className="font-semibold">{shown.name}</span> actualizó tu semana
                <span className="sr-only"> · </span>
              </p>
              <div className="mt-0.5 flex items-center justify-between gap-3">
                <time
                  dateTime={at.toISOString()}
                  title={at.toLocaleString("es-PE", { dateStyle: "full", timeStyle: "short" })}
                  className="text-[13px] text-muted-foreground tabular-nums"
                >
                  {formatEditedAt(at, now)}
                </time>
                <Button variant="outline" size="sm" className="shrink-0" onClick={acknowledge}>
                  Entendido
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
